/**
 * Grade horária da Matriz Mensal — blocos hora × dia com cards de atividade.
 */

import type { LstCargo, LstExecucao, LstFrequency, LstTemplate } from '../types';
import { isTemplateDueOnDate } from './lstSchedule';
import { isLstSlotStillOpen } from '../utils/lstEffectiveStatus';

/** Início/fim padrão do eixo quando há atividades (cobre turno estendido). */
export const DEFAULT_AXIS_START_HOUR = 6;
export const DEFAULT_AXIS_END_HOUR = 18;

export type HourLoadState = 'idle' | 'partial' | 'saturated' | 'overload';
export type HourActivityCardState = 'ok' | 'desvio' | 'perdido' | 'futuro';

export type LstMatrixBlock = 'diaria' | 'semanal' | 'mensal';

export interface HourSlot {
  hour: number;
  label: string;
}

export interface TemplateCodeRef {
  code: string;
  block: LstMatrixBlock;
}

export interface HourCellActivity {
  template: LstTemplate;
  code: string;
  block: LstMatrixBlock;
  cardState: HourActivityCardState;
  overlapMin: number;
  execucao: LstExecucao | null;
}

export interface HourCellData {
  date: string;
  hour: number;
  hourLabel: string;
  loadMin: number;
  state: HourLoadState;
  activities: HourCellActivity[];
}

function blockOf(freq: LstFrequency): LstMatrixBlock {
  if (freq === 'semanal') return 'semanal';
  if (freq === 'mensal') return 'mensal';
  return 'diaria';
}

/** Todas as rotinas do cargo que possuem posição na linha do tempo. */
export function getHourlyTemplates(templates: LstTemplate[], cargo: LstCargo): LstTemplate[] {
  return templates.filter((t) => t.target_role === cargo);
}

/**
 * Mapa templateId → código (D1, S1, M1…) — mesma numeração da visão por atividades.
 */
export function buildTemplateCodeMap(
  templates: LstTemplate[],
  cargo: LstCargo,
): Map<string, TemplateCodeRef> {
  const byBlock: Record<LstMatrixBlock, LstTemplate[]> = { diaria: [], semanal: [], mensal: [] };
  for (const t of templates) {
    if (t.target_role !== cargo) continue;
    byBlock[blockOf(t.frequency)].push(t);
  }

  const sortFn = (a: LstTemplate, b: LstTemplate) => {
    const ta = a.scheduled_time ?? '';
    const tb = b.scheduled_time ?? '';
    if (ta && tb && ta !== tb) return ta < tb ? -1 : 1;
    return a.sort_order - b.sort_order;
  };

  const prefixes: Record<LstMatrixBlock, string> = { diaria: 'D', semanal: 'S', mensal: 'M' };
  const order: LstMatrixBlock[] = ['diaria', 'semanal', 'mensal'];
  const map = new Map<string, TemplateCodeRef>();

  for (const block of order) {
    byBlock[block].sort(sortFn).forEach((t, i) => {
      map.set(t.id, { code: `${prefixes[block]}${i + 1}`, block });
    });
  }
  return map;
}

/**
 * Extrai hora e minuto de strings vindas do banco: "08:00", "08:30", "08:00:00".
 */
export function parseScheduledTime(time: string | null | undefined): { hour: number; minute: number } | null {
  if (!time || typeof time !== 'string') return null;
  const trimmed = time.trim();
  const parts = trimmed.split(':');
  if (parts.length < 2) return null;
  const hour = parseInt(parts[0], 10);
  const minute = parseInt(parts[1], 10);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return { hour, minute };
}

/** Normaliza para HH:MM (compatível com input type="time" e Postgres TIME). */
export function normalizeScheduledTime(time: string | null | undefined): string | null {
  const parsed = parseScheduledTime(time);
  if (!parsed) return time?.trim() || null;
  return `${String(parsed.hour).padStart(2, '0')}:${String(parsed.minute).padStart(2, '0')}`;
}

/** Alias semântico para exibição rígida em listagens e matriz (24h HH:mm). */
export const formatTimeHHmm = normalizeScheduledTime;

/** Hora inteira do bloco vertical (ex: "08:30" → 8). */
export function resolveTemplateHour(template: LstTemplate): number {
  const parsed = parseScheduledTime(template.scheduled_time);
  if (parsed) return parsed.hour;
  const periodHour: Record<LstTemplate['period'], number> = {
    inicio: 7,
    meio: 10,
    fim: 16,
  };
  return periodHour[template.period];
}

function hourLabel(h: number): string {
  return `${String(h).padStart(2, '0')}:00`;
}

/**
 * Eixo vertical dinâmico: cobre do menor ao maior horário cadastrado,
 * com margem padrão 06:00–18:00 (sem teto fixo em 13h).
 */
export function buildShiftHourSlots(templates: LstTemplate[], cargo: LstCargo): HourSlot[] {
  const hourly = getHourlyTemplates(templates, cargo);
  const activityHours = hourly.map(resolveTemplateHour);

  if (activityHours.length === 0) {
    const hours: number[] = [];
    for (let h = DEFAULT_AXIS_START_HOUR; h <= DEFAULT_AXIS_END_HOUR; h++) hours.push(h);
    return hours.map((hour) => ({ hour, label: hourLabel(hour) }));
  }

  const minActivity = Math.min(...activityHours);
  const maxActivity = Math.max(...activityHours);
  const startHour = Math.min(DEFAULT_AXIS_START_HOUR, minActivity);
  const endHour = Math.max(DEFAULT_AXIS_END_HOUR, maxActivity + 1);

  const hours: number[] = [];
  for (let h = startHour; h <= endHour; h++) hours.push(h);
  return hours.map((hour) => ({ hour, label: hourLabel(hour) }));
}

function classifyLoad(min: number): HourLoadState {
  if (min <= 0) return 'idle';
  if (min > 60) return 'overload';
  if (min >= 45) return 'saturated';
  return 'partial';
}

function cellKey(date: string, hour: number): string {
  return `${date}__${hour}`;
}

/** Status do card — mesma regra da visão por atividades / quadro físico. */
export function resolveActivityCardState(
  block: LstMatrixBlock,
  date: string,
  today: string,
  exec: LstExecucao | null,
  scheduledTime?: string | null,
): HourActivityCardState {
  const stillOpen = isLstSlotStillOpen(scheduledTime, date, today);
  if (exec) {
    if (exec.status === 'realizado') return 'ok';
    if (exec.status === 'realizado_com_desvio') return 'desvio';
    if (exec.status === 'nao_realizado') return 'perdido';
  }
  if (stillOpen) return 'futuro';
  return block === 'diaria' ? 'perdido' : 'futuro';
}

/** Nome resumido para o card compacto. */
export function shortenActivityTitle(title: string, maxLen = 22): string {
  const t = title.trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen - 1)}…`;
}

/**
 * Pré-computa toda a grade horária (dia × hora).
 * Alocação por hora inteira: "08:30" → bloco "08:00".
 */
export function buildHourlyGrid(
  templates: LstTemplate[],
  cargo: LstCargo,
  monthDates: string[],
  execucoes: LstExecucao[],
  userId: string,
  today: string,
): Map<string, HourCellData> {
  const slots = buildShiftHourSlots(templates, cargo);
  const hourly = getHourlyTemplates(templates, cargo);
  const codeMap = buildTemplateCodeMap(templates, cargo);

  const execIndex = new Map<string, LstExecucao>();
  for (const e of execucoes) {
    if (e.user_id === userId) execIndex.set(`${e.template_id}__${e.data}`, e);
  }

  const byHour = new Map<number, LstTemplate[]>();
  for (const t of hourly) {
    const hour = resolveTemplateHour(t);
    const list = byHour.get(hour) ?? [];
    list.push(t);
    byHour.set(hour, list);
  }

  const grid = new Map<string, HourCellData>();

  for (const date of monthDates) {
    for (const slot of slots) {
      const templatesInHour = (byHour.get(slot.hour) ?? []).filter((template) =>
        isTemplateDueOnDate(template, date, monthDates),
      );
      const activities: HourCellActivity[] = templatesInHour.map((template) => {
        const ref = codeMap.get(template.id) ?? { code: '?', block: blockOf(template.frequency) };
        const execucao = execIndex.get(`${template.id}__${date}`) ?? null;
        return {
          template,
          code: ref.code,
          block: ref.block,
          cardState: resolveActivityCardState(ref.block, date, today, execucao, template.scheduled_time),
          overlapMin: Math.max(5, template.duration_minutes ?? 15),
          execucao,
        };
      });

      const loadMin = activities.reduce((s, a) => s + a.overlapMin, 0);
      grid.set(cellKey(date, slot.hour), {
        date,
        hour: slot.hour,
        hourLabel: slot.label,
        loadMin,
        state: classifyLoad(loadMin),
        activities,
      });
    }
  }

  return grid;
}

/** Verifica se alguma atividade do bloco dispara alerta antecipado hoje. */
export function hourBlockHasUpcomingAlert(
  cell: HourCellData,
  today: string,
  nowMin: number,
): boolean {
  if (cell.date !== today || cell.activities.length === 0) return false;
  for (const a of cell.activities) {
    const parsed = parseScheduledTime(a.template.scheduled_time);
    if (!parsed) continue;
    const target = parsed.hour * 60 + parsed.minute;
    const lead = a.template.alert_lead_minutes ?? 10;
    const diff = target - nowMin;
    if (diff > 0 && diff <= lead) return true;
  }
  return false;
}
